import { CreditCard, Pix, ReceiptLongOutlined } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
	Box,
	Checkbox,
	Chip,
	IconButton,
	ListItemText,
	Menu,
	MenuItem,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Tooltip,
	Typography,
} from "@mui/material";
import { MouseEvent, useState } from "react";
import { Environment } from "../../../shared/environment";
import { ISetupCharge, TSetupChargeAction } from "../../../shared/services/api/setupCharge/Types";
import { globalMaskDocument } from "../../../shared/utils";
import { GatewayLogo } from "../../client/components/createComponents/GatewaySelect";
import { setupChargeFormatCurrency, setupChargeFormatDate } from "../utils/setupChargeFormat";
import { setupChargePaymentMethodLabel, setupChargeStatusColor, setupChargeStatusLabel } from "../utils/setupChargeStatus";

export type TSetupChargeRowAction = TSetupChargeAction | "detail" | "copy_link";

interface TableSetupChargeProps {
	rows: ISetupCharge[];
	page: number;
	rowsPerPage: number;
	totalCount: number;
	isLoading?: boolean;
	/** Quem pode agir nas cobranças (ações operacionais). Sem isso o menu só mostra os detalhes. */
	canManage: boolean;
	selectedIds: string[];
	onSelectionChange: (ids: string[]) => void;
	onAction: (action: TSetupChargeRowAction, row: ISetupCharge) => void;
	onPageChange: (newPage: number) => void;
	onRowsPerPageChange: (newRowsPerPage: number) => void;
}

const COLUMNS: { label: string; align?: "left" | "right" | "center"; minWidth?: number }[] = [
	{ label: "Data", minWidth: 90 },
	{ label: "Imobiliária", minWidth: 180 },
	{ label: "Pagador", minWidth: 180 },
	{ label: "Vencimento", minWidth: 100 },
	{ label: "Valor / repasse", align: "right", minWidth: 130 },
	{ label: "Status", minWidth: 120 },
	{ label: "Gateway / Pagamento", align: "center", minWidth: 150 },
];

const MENU_ACTIONS: { key: TSetupChargeAction; label: string }[] = [
	{ key: "resend", label: "Reenviar link" },
	{ key: "contact_update", label: "Corrigir contato" },
	{ key: "cancel", label: "Cancelar taxa" },
	{ key: "manual_payment", label: "Dar baixa manual" },
	{ key: "repass", label: "Registrar repasse" },
	{ key: "reprocess", label: "Reprocessar" },
];

/** Sem `allowedActions` na resposta, libera o item: a API recusa com o motivo se não puder. */
export const setupChargeActionState = (row: ISetupCharge, action: TSetupChargeAction): { allowed: boolean; message?: string } => {
	const decision = row.allowedActions?.[action];
	return decision ? { allowed: !!decision.allowed, message: decision.message } : { allowed: true };
};

const PaymentIcon = ({ method }: { method?: string | null }) => {
	const label = setupChargePaymentMethodLabel(method);
	const normalized = (method ?? "").toLowerCase();
	let icon: React.ReactNode = null;
	if (normalized === "pix") icon = <Pix sx={{ color: "#7bdcb5", fontSize: "large" }} />;
	else if (normalized === "credit_card" || normalized === "boleto_credit_card") icon = <CreditCard fontSize="small" color="action" />;
	else if (normalized === "boleto") icon = <ReceiptLongOutlined fontSize="small" color="action" />;
	if (!icon) return null;
	return (
		<Tooltip title={`Pagamento: ${label}`} placement="top">
			<Box component="span" sx={{ display: "flex", alignItems: "center" }}>
				{icon}
			</Box>
		</Tooltip>
	);
};

export const TableSetupCharge = ({
	rows,
	page,
	rowsPerPage,
	totalCount,
	isLoading = false,
	canManage,
	selectedIds,
	onSelectionChange,
	onAction,
	onPageChange,
	onRowsPerPageChange,
}: TableSetupChargeProps) => {
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
	const [menuRow, setMenuRow] = useState<ISetupCharge | null>(null);

	const selectableRows = rows.filter(
		(row) => canManage && (setupChargeActionState(row, "cancel").allowed || setupChargeActionState(row, "resend").allowed || setupChargeActionState(row, "repass").allowed),
	);
	const allSelected = selectableRows.length > 0 && selectableRows.every((row) => selectedIds.includes(row.id));

	const handleMenuOpen = (event: MouseEvent<HTMLElement>, row: ISetupCharge) => {
		setMenuAnchor(event.currentTarget);
		setMenuRow(row);
	};
	const handleMenuClose = () => {
		setMenuAnchor(null);
		setMenuRow(null);
	};
	const handleMenuAction = (action: TSetupChargeRowAction) => {
		if (menuRow) onAction(action, menuRow);
		handleMenuClose();
	};

	const toggleRow = (id: string, checked: boolean) => onSelectionChange(checked ? [...selectedIds, id] : selectedIds.filter((selected) => selected !== id));
	const toggleAll = (checked: boolean) => onSelectionChange(checked ? selectableRows.map((row) => row.id) : []);

	return (
		<TableContainer component={Paper} sx={{ mt: 1, width: "96%", px: { xs: 1, sm: 3 }, py: { xs: 2, sm: 3 }, ml: 1, borderRadius: 2, overflow: "auto", maxHeight: "70vh" }}>
			<Table size="small" stickyHeader>
				<TableHead>
					<TableRow>
						<TableCell padding="checkbox" sx={{ backgroundColor: "grey.100" }}>
							{canManage && <Checkbox size="small" checked={allSelected} indeterminate={!allSelected && selectedIds.length > 0} onChange={(e) => toggleAll(e.target.checked)} />}
						</TableCell>
						{COLUMNS.map((column) => (
							<TableCell key={column.label} align={column.align ?? "left"} sx={{ backgroundColor: "grey.100", minWidth: column.minWidth }}>
								<Typography variant="button">{column.label}</Typography>
							</TableCell>
						))}
						<TableCell align="center" sx={{ backgroundColor: "grey.100" }} />
					</TableRow>
				</TableHead>
				<TableBody>
					{rows.length === 0 && (
						<TableRow>
							<TableCell colSpan={COLUMNS.length + 2}>
								<Typography variant="body2" color="text.secondary" align="center">
									{isLoading ? "Carregando..." : Environment.LISTAGEM_VAZIA}
								</Typography>
							</TableCell>
						</TableRow>
					)}
					{rows.map((row) => {
						const selectable = selectableRows.some((candidate) => candidate.id === row.id);
						return (
							<TableRow key={row.id} hover selected={selectedIds.includes(row.id)} sx={{ cursor: "pointer" }} onClick={() => onAction("detail", row)}>
								<TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
									{selectable && <Checkbox size="small" checked={selectedIds.includes(row.id)} onChange={(e) => toggleRow(row.id, e.target.checked)} />}
								</TableCell>
								<TableCell>{setupChargeFormatDate(row.createdAt)}</TableCell>
								<TableCell>
									<Typography fontWeight="bold">{row.clientName ?? ""}</Typography>
									<Typography variant="body2" color="textSecondary">
										{globalMaskDocument(row.clientDocument ?? "")}
									</Typography>
								</TableCell>
								<TableCell>
									<Typography fontWeight="bold">{row.payerName ?? ""}</Typography>
									<Typography variant="body2" color="textSecondary">
										{globalMaskDocument(row.payerDocument ?? "")}
									</Typography>
								</TableCell>
								<TableCell>{setupChargeFormatDate(row.expiresAt)}</TableCell>
								<TableCell align="right">
									<Typography fontWeight="bold">{setupChargeFormatCurrency(row.amount)}</Typography>
									{row.expectedRepass !== undefined && row.expectedRepass !== null && (
										<Typography variant="caption" color="textSecondary">
											Repasse {setupChargeFormatCurrency(row.repassed ? row.repassAmount : row.expectedRepass)}
											{row.repassed ? " (feito)" : ""}
										</Typography>
									)}
								</TableCell>
								<TableCell>
									<Box display="flex" alignItems="center" gap={0.5}>
										<Chip size="small" label={setupChargeStatusLabel(row.status)} color={setupChargeStatusColor(row.status)} variant="outlined" />
										{row.duplicatePayment && (
											<Tooltip title="Pagamento em duplicidade: confira antes de repassar">
												<ErrorOutlineIcon fontSize="small" color="error" />
											</Tooltip>
										)}
									</Box>
								</TableCell>
								<TableCell align="center">
									{row.gateway || row.paymentMethod ? (
										<Box display="flex" alignItems="center" justifyContent="center" gap={1}>
											<GatewayLogo gateway={row.gateway} />
											<PaymentIcon method={row.paymentMethod} />
										</Box>
									) : (
										"-"
									)}
								</TableCell>
								<TableCell align="center" onClick={(e) => e.stopPropagation()}>
									<IconButton size="small" onClick={(e) => handleMenuOpen(e, row)} aria-label="Ações">
										<MoreVertIcon fontSize="small" />
									</IconButton>
								</TableCell>
							</TableRow>
						);
					})}
				</TableBody>
			</Table>

			<TablePagination
				component="div"
				count={totalCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={(_, newPage) => onPageChange(newPage)}
				onRowsPerPageChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
				rowsPerPageOptions={[10, 25, 50]}
				labelRowsPerPage="Linhas por página:"
				labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count} registros`}
			/>

			<Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={handleMenuClose}>
				<MenuItem onClick={() => handleMenuAction("detail")}>Ver detalhes</MenuItem>
				{menuRow?.paymentLink && <MenuItem onClick={() => handleMenuAction("copy_link")}>Copiar link de pagamento</MenuItem>}
				{canManage &&
					menuRow &&
					MENU_ACTIONS.map(({ key, label }) => {
						const state = setupChargeActionState(menuRow, key);
						const item = (
							<MenuItem key={key} disabled={!state.allowed} onClick={() => handleMenuAction(key)}>
								<ListItemText>{label}</ListItemText>
							</MenuItem>
						);
						return state.allowed || !state.message ? (
							item
						) : (
							<Tooltip key={key} title={state.message} placement="left">
								<span>{item}</span>
							</Tooltip>
						);
					})}
			</Menu>
		</TableContainer>
	);
};
